import React, { useEffect, useState } from 'react';

import { Badge, Box, Icon, Spinner } from '@amzn/awsui-components-react-v3';

import { AsyncState, Model, ReceivedLineItemExecutionStatus, ReceiveExecutionStatus } from '../../store/types';
import { ExecutionProgress } from './ExecutionProgress';
import { EXECUTION_ERROR_STATES } from './constants';
import useExecutionStatus from './useExecutionStatus';
import { getSerialList } from './util';

interface ExecutionStatusProps {
  model: Model;
  progressBar?: boolean;
  refetch?: boolean;
}

export const ExecutionStatus = ({ model, progressBar = false, refetch = true }: ExecutionStatusProps) => {
  const [fetched, setFetched] = useState([AsyncState.OK, AsyncState.ERROR].includes(model?.status));

  const onCompleted = () => setFetched(true);

  const { executionStatus, executionStatusResponse, loading, error, called, state } = useExecutionStatus({
    onCompleted,
  });
  useEffect(() => {
    if (model?.receiveExecutionUUId && ![AsyncState.OK, AsyncState.ERROR].includes(model?.status)) {
      executionStatus({
        variables: {
          receiveExecutionUuId: model?.receiveExecutionUUId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!model || !model?.receiveExecutionUUId) {
    return null;
  }

  const lineItem: ReceivedLineItemExecutionStatus = executionStatusResponse?.receivedLineItemExecutionStatusList?.[0];

  const failedSerialsList = getSerialList(lineItem, (s) => EXECUTION_ERROR_STATES.includes(s.serialStatus));

  const pendingSerialsList = getSerialList(
    lineItem,
    (s) =>
      !EXECUTION_ERROR_STATES.includes(s.serialStatus) &&
      s.serialStatus !== ReceiveExecutionStatus.SUCCESSFULLY_COMPLETED_RECEIVE,
  );

  const succeededSerialsList = getSerialList(
    lineItem,
    (s) => s.serialStatus === ReceiveExecutionStatus.SUCCESSFULLY_COMPLETED_RECEIVE,
  );

  let executionState = state;
  if (fetched && model?.status) {
    executionState = model?.status;
  }

  if (progressBar) {
    return (
      <ExecutionProgress
        errorsList={failedSerialsList}
        receivedList={succeededSerialsList}
        pendingList={pendingSerialsList}
        executionId={model?.receiveExecutionUUId}
        loading={!called}
      />
    );
  }

  // error fetching the status
  if (!!error) {
    return (
      <Box margin={{ left: 's' }} variant="span">
        <Badge color="red">
          <Icon name="status-negative" /> Error Fetching the status
        </Badge>
      </Box>
    );
  }

  if (executionState === AsyncState.ERROR) {
    return (
      <Box margin={{ left: 's' }}>
        <Badge color="red">
          <Icon name="status-negative" /> Failed
        </Badge>
      </Box>
    );
  }

  if ((executionState === AsyncState.IN_PROGRESS || loading) && model?.receiveExecutionUUId) {
    return (
      <Box margin={{ left: 's' }}>
        <Badge>
          <Spinner size="normal" /> {AsyncState.IN_PROGRESS}
        </Badge>
      </Box>
    );
  }

  if (executionState === AsyncState.OK && !loading) {
    return (
      <Badge color="green">
        <Icon name="status-positive" /> Received
      </Badge>
    );
  }

  return null;
};
